/**
 * Utility functions for PQ Ingestion
 */

import { JobStatus, UIStatus } from "~/types-and-enums/pqIngestionTypes";
import { UnitTypeEnum } from "~/types-and-enums/generalTypes";
import { formatDateToYYYYMMDD } from "~/utils/dateUtils";
import { formatIncotermLocationShort, legacyIncotermLocation } from "~/utils/incotermLocationUtils";

/**
 * Infer available quantity unit and value from maximum_available_quantity
 */
export function inferAvailableQuantityFromMax(
  max: number,
  unitsPerContainer?: number,
  unitsPerPallet?: number
): { unit: UnitTypeEnum; quantity: string } {
  const uc = unitsPerContainer ?? 0;
  const up = unitsPerPallet ?? 0;
  if (uc > 0 && max % uc === 0) {
    return { unit: UnitTypeEnum.containers, quantity: String(max / uc) };
  }
  if (up > 0 && max % up === 0) {
    return { unit: UnitTypeEnum.pallets, quantity: String(max / up) };
  }
  return { unit: UnitTypeEnum.pieces, quantity: String(max) };
}

/**
 * Infer MOQ unit and value from minimum_order_quantity
 */
export function inferMoqFromMin(
  min: number,
  unitsPerContainer?: number,
  unitsPerPallet?: number
): { unit: UnitTypeEnum; quantity: string } {
  const uc = unitsPerContainer ?? 0;
  const up = unitsPerPallet ?? 0;
  if (uc > 0 && min % uc === 0) {
    return { unit: UnitTypeEnum.containers, quantity: String(min / uc) };
  }
  if (up > 0 && min % up === 0) {
    return { unit: UnitTypeEnum.pallets, quantity: String(min / up) };
  }
  return { unit: UnitTypeEnum.pieces, quantity: String(min) };
}

/**
 * Map API job status to UI-friendly status
 */
export const mapJobStatusToUIStatus = (status: JobStatus): UIStatus => {
  switch (status) {
    case "FINALIZED":
      return "FINALIZED";
    case "NEEDS_REVIEW":
      return "NEEDS_REVIEW";
    case "IN_PROGRESS":
    case "CREATED":
      return "IN_PROGRESS";
    case "FAILED":
      return "FAILED";
    default:
      return "NEEDS_REVIEW";
  }
};

/**
 * Format date from ISO string to readable format
 * Converts UTC/ISO date to browser's local timezone
 * @param isoString - ISO date string (e.g., "2026-01-28T12:40:08.278199475Z" or "2026-01-28T12:40:08.278199475")
 * @returns Formatted date string in local timezone (e.g., "Jan 15, 2026, 2:27 PM")
 */
export const formatPQIngestionDate = (isoString: string): string => {
  // If the ISO string doesn't have timezone info (no 'Z' or offset),
  // and it's from the backend, it's likely UTC. Append 'Z' to treat it as UTC.
  // If it already has timezone info, new Date() will handle it correctly.
  let dateString = isoString.trim();

  // Check if the string already has timezone info (ends with Z, +, or -)
  const hasTimezone = /[Z+-]\d{2}:?\d{2}$/.test(dateString) || dateString.endsWith("Z");

  // If no timezone info, assume it's UTC from the backend and append 'Z'
  if (!hasTimezone && dateString.includes("T")) {
    dateString = dateString.endsWith("Z") ? dateString : dateString + "Z";
  }

  const date = new Date(dateString);

  // Validate the date is valid
  if (isNaN(date.getTime())) {
    console.error("Invalid date string:", isoString);
    return isoString; // Return original string if invalid
  }

  // toLocaleString automatically converts UTC to browser's local timezone
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Get action label based on UI status
 * @param status - UI status
 * @returns Action label string
 */
export const getPQIngestionActionLabel = (status: UIStatus): string => {
  // FINALIZED, NEEDS_REVIEW, and default all return "View PQs"
  if (status === "IN_PROGRESS") {
    return "Processing...";
  }
  if (status === "FAILED") {
    return "Retry";
  }
  return "View PQs";
};

/**
 * Map UI status back to API job status
 */
export const mapUIStatusToJobStatus = (uiStatus: UIStatus): JobStatus => {
  switch (uiStatus) {
    case "FINALIZED":
      return "FINALIZED";
    case "NEEDS_REVIEW":
      return "NEEDS_REVIEW";
    case "IN_PROGRESS":
      // CREATED is equal to PROCESSING
      return "CREATED";
    case "FAILED":
      return "FAILED";
    default:
      return "NEEDS_REVIEW";
  }
};

/**
 * Status badge class names mapping
 */
export const submissionStatusClassName: Record<UIStatus, string> = {
  FINALIZED: "bg-green-100 text-green-700",
  NEEDS_REVIEW: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  FAILED: "bg-red-100 text-red-700",
};

/**
 * Helper function to get display value for a column
 * @param row - The PQ candidate row
 * @param columnKey - The column key to get the value for
 * @param translate - Translation function
 * @param formatGermanDate - Function to format dates in German format
 * @param isEmptyValue - Function to check if a value is empty
 * @param addCommaToNumber - Function to add commas to numbers
 * @returns The display value as a string
 */
const PQ_TYPE_ALWAYS = "ALWAYS_IN_STOCK";
const PQ_TYPE_UPCOMING = "UPCOMING_STOCK";
const PQ_TYPE_IN_STOCK_NOW = "IN_STOCK_NOW";

export const getColumnDisplayValue = (
  row: Record<string, unknown> & {
    rowId: string;
    status: "IN_REVIEW" | "NOT_CREATED" | "CREATED" | "FAILED";
    salesforce_product?: unknown;
    unit_price?: number | string | null;
    earliest_available_date?: string | null;
    valid_until?: string | null;
    incoterm?: string | null;
    incoterm_location_city?: string | null;
    incoterm_location_country?: string | null;
    incoterm_location_postal_code?: string | null;
    pq_type?: string | null;
    delivery_time_in_days?: number | null;
  },
  columnKey: string,
  translate: (key: string, params?: Record<string, unknown>) => string,
  formatGermanDate: (dateString: string | null | undefined) => string,
  isEmptyValue: (value: unknown) => boolean,
  addCommaToNumber: (number: number) => string
): string => {
  if (columnKey === "stock_availability") {
    const pqType = row.pq_type;
    const label =
      pqType === PQ_TYPE_ALWAYS
        ? translate("pq-ingestion.stock-availability.always-in-stock")
        : pqType === PQ_TYPE_IN_STOCK_NOW
          ? translate("pq-ingestion.stock-availability.in-stock-now")
          : pqType === PQ_TYPE_UPCOMING
            ? translate("pq-ingestion.stock-availability.upcoming-stock")
            : "";
    const availableFrom =
      pqType === PQ_TYPE_ALWAYS
        ? row.delivery_time_in_days != null
          ? translate("pq-ingestion.stock-availability.available-from-days", {
              count: Number(row.delivery_time_in_days),
            })
          : ""
        : pqType === PQ_TYPE_IN_STOCK_NOW
          ? `${translate("pq-ingestion.stock-availability.av-from")} ${formatGermanDate(formatDateToYYYYMMDD(new Date()))}`
          : row.earliest_available_date && String(row.earliest_available_date).trim()
            ? `${translate("pq-ingestion.stock-availability.av-from")} ${formatGermanDate(row.earliest_available_date as string)}`
            : "";
    if (!label) return translate("pq-ingestion.empty");
    return availableFrom ? `${label} · ${availableFrom}` : label;
  }
  if (columnKey === "status") {
    return row.status;
  } else if (columnKey === "salesforce_product") {
    // Check for empty in multiple ways: null, undefined, empty object, or object with empty name
    const product: unknown = row.salesforce_product;
    if (!product || isEmptyValue(product)) {
      return translate("pq-ingestion.empty");
    }
    if (typeof product === "object" && product !== null && "name" in product) {
      const name = (product as { name?: string | null }).name;
      if (!name || (typeof name === "string" && name.trim() === "")) {
        return translate("pq-ingestion.empty");
      }
      return name;
    }
    if (typeof product === "string") {
      const productStr: string = product;
      if (productStr.trim() === "") {
        return translate("pq-ingestion.empty");
      }
      return productStr;
    }
    return translate("pq-ingestion.empty");
  } else if (columnKey === "unit_price") {
    const price = row.unit_price;
    if (price === null || price === undefined || price === "" || (typeof price === "string" && price.trim() === "")) {
      return translate("pq-ingestion.empty");
    }
    // Format price with comma and Euro sign: e.g., 43,381.72 €
    const numPrice = typeof price === "string" ? parseFloat(price) : Number(price);
    if (isNaN(numPrice)) {
      return translate("pq-ingestion.empty");
    }
    return `${addCommaToNumber(numPrice)} €`;
  } else if (columnKey === "lead_time") {
    const date = row.earliest_available_date;
    if (!date || (typeof date === "string" && date.trim() === "")) {
      return translate("pq-ingestion.empty");
    }
    return formatGermanDate(date);
  } else if (columnKey === "incoterm") {
    const incoterm = row.incoterm;
    if (!incoterm || (typeof incoterm === "string" && incoterm.trim() === "")) {
      return translate("pq-ingestion.empty");
    }
    return incoterm;
  } else if (columnKey === "incoterm_location") {
    const location = legacyIncotermLocation({
      postal_code: row.incoterm_location_postal_code,
      city: row.incoterm_location_city,
      country: row.incoterm_location_country,
    });
    return formatIncotermLocationShort(location) || translate("pq-ingestion.empty");
  } else if (columnKey === "valid_until") {
    const dateValue = row.valid_until;
    if (!dateValue || (typeof dateValue === "string" && dateValue.trim() === "")) {
      return translate("pq-ingestion.empty");
    }
    return formatGermanDate(dateValue);
  } else {
    const rawValue = (row as Record<string, unknown>)[columnKey];
    if (isEmptyValue(rawValue)) {
      return translate("pq-ingestion.empty");
    }
    return String(rawValue);
  }
};
